import React, { useEffect, useMemo, useState } from 'react';
import { Box, Divider, Tab, Tabs, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store';
import {
  fetchByUser,
  fetchConnectivity,
  fetchFailures,
  fetchPair,
  fetchScores,
  fetchSummary,
  setFilters,
} from '../../store/callStats/actionCreators';
import {
  CallStatsFilters,
  Participant,
} from '../../store/callStats/callStatsTypes';
import TimeRangeFilter from './filters/TimeRangeFilter';
import UserPairFilter from './filters/UserPairFilter';
import OverviewSection from './sections/OverviewSection';
import QualitySection from './sections/QualitySection';
import ConnectivitySection from './sections/ConnectivitySection';
import FailuresSection from './sections/FailuresSection';
import CallLogSection from './sections/CallLogSection';
import PairSection from './sections/PairSection';
import {
  darkPurple,
  dimGrey,
  lightBackground2,
  lightGrey,
} from '../../mainTheme';

const TABS = [
  'Overview',
  'Quality',
  'Connectivity',
  'Failures',
  'Call log',
] as const;

const CallStatsDashboard: React.FC = () => {
  const dispatch = useDispatch();
  const { filters, byUser } = useSelector(
    (state: RootState) => state.callStats,
  );
  const [tab, setTab] = useState(0);

  const pairActive = Boolean(filters.userId && filters.peerUserId);

  // Page-owned panels: refetch whenever the filter set changes.
  useEffect(() => {
    dispatch(fetchSummary(filters) as never);
    dispatch(fetchByUser(filters) as never);
    dispatch(fetchFailures(filters) as never);
    dispatch(fetchConnectivity(filters) as never);
    dispatch(fetchScores(filters) as never);
  }, [dispatch, filters]);

  useEffect(() => {
    if (pairActive) dispatch(fetchPair(filters) as never);
  }, [dispatch, filters, pairActive]);

  const onFilterChange = (patch: Partial<CallStatsFilters>) =>
    dispatch(setFilters(patch));

  const userOptions: Participant[] = useMemo(
    () =>
      (byUser.data?.users ?? []).map((u) => ({
        userId: u.userId,
        displayName: u.displayName,
      })),
    [byUser.data],
  );

  return (
    <Box sx={{ p: 3, backgroundColor: lightBackground2, minHeight: '100%' }}>
      <Box mb={1}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: darkPurple }}>
          Call Stats
        </Typography>
        <Typography variant="body2" sx={{ color: dimGrey }}>
          Janus / WebRTC call telemetry · engineering view
        </Typography>
        <Typography variant="caption" sx={{ color: lightGrey }}>
          All panels read live call telemetry. Quality/connectivity are sparse
          until hubs POST call-quality summaries on hangup.
        </Typography>
      </Box>

      <Box
        display="flex"
        gap={2}
        flexWrap="wrap"
        justifyContent="space-between"
        alignItems="center"
        py={2}
      >
        <TimeRangeFilter
          window={filters.window}
          from={filters.from}
          to={filters.to}
          onChange={onFilterChange}
        />
        <UserPairFilter
          options={userOptions}
          userId={filters.userId}
          peerUserId={filters.peerUserId}
          onChange={onFilterChange}
        />
      </Box>

      {pairActive && (
        <Box mb={2}>
          <PairSection />
        </Box>
      )}

      <Divider sx={{ mb: 1 }} />
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        textColor="secondary"
        indicatorColor="secondary"
      >
        {TABS.map((t) => (
          <Tab key={t} label={t} />
        ))}
      </Tabs>

      <Box mt={2}>
        {tab === 0 && <OverviewSection />}
        {tab === 1 && <QualitySection />}
        {tab === 2 && <ConnectivitySection />}
        {tab === 3 && <FailuresSection />}
        {tab === 4 && <CallLogSection />}
      </Box>
    </Box>
  );
};

export default CallStatsDashboard;
